import {AntDesign, MaterialCommunityIcons} from '@expo/vector-icons';
import {StackScreenProps} from '@react-navigation/stack';
import {createVeriffFrame} from '@veriff/incontext-sdk';
import {Box, Button, Checkbox, Text} from 'native-base';
import React, {useState} from 'react';
import {SafeAreaView} from 'react-native-safe-area-context';
import {colors, t} from 'react-native-tailwindcss';
import {IdStatus, useCreateIdSessionMutation} from '../api/generated';
import {useAccountContext} from '../lib/accountContext';
import i18n from '../lib/i18n';
import {Nav, NavParams} from './navigation';
import {Anchor} from '../components/Anchor';

export const IdSessionScreen: React.FC<StackScreenProps<NavParams, Nav.IdSessionScreen>> = ({
  navigation
}) => {
  const account = useAccountContext();
  const [isPolicyAccepted, setPolicyAccepted] = useState(false);
  const [createIdSession, {loading}] = useCreateIdSessionMutation();

  const handleCreateIdSession = async () => {
    const {data} = await createIdSession();
    if (data?.createIdSession) {
      createVeriffFrame({url: data.createIdSession.idSessionUrl, lang: i18n.locale});
    }
  };

  if (!account) return null;
  const {idStatus} = account;

  if (idStatus === IdStatus.SUBMITTED) {
    return (
      <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
        <Box style={[t.flex1]}>
          <Box style={[t.textCenter, t.mX6, t.mY12]}>
            <MaterialCommunityIcons
              name="account-clock"
              size={80}
              color={colors.gray500}
              style={[t.mB4]}
            />
            <Text>{i18n.t('idSessionSubmitted')}</Text>
          </Box>
        </Box>
      </SafeAreaView>
    );
  }

  if (idStatus && [IdStatus.DECLINED, IdStatus.RESUBMISSION_REQUESTED].includes(idStatus)) {
    return (
      <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
        <Box style={[t.flex1]}>
          <Box style={[t.textCenter, t.mX6, t.mY12]}>
            <MaterialCommunityIcons
              name="account-cancel"
              size={80}
              color={colors.red500}
              style={[t.mB4]}
            />
            <Text>
              {i18n.t('idSessionDeclined', {statusMessage: account?.idStatusMessage || ''})}
            </Text>
          </Box>
        </Box>
        <Button style={[t.m4]} onPress={handleCreateIdSession} isLoading={loading}>
          {i18n.t('retryIdSession')}
        </Button>
      </SafeAreaView>
    );
  }

  if (idStatus === IdStatus.APPROVED) {
    return (
      <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
        <Box style={[t.flex1]}>
          <Box style={[t.textCenter, t.mX6, t.mY12]}>
            <MaterialCommunityIcons
              name="account-check"
              size={80}
              color={colors.green500}
              style={[t.mB4]}
            />
            <Text>{i18n.t('idSessionApproved')}</Text>
          </Box>
        </Box>
        <Button style={[t.m4, t.mT4]} onPress={() => navigation.navigate(Nav.HomeScreen)}>
          {i18n.t('getStarted')}
        </Button>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <Box style={[t.flex1]}>
        <Box style={[t.textCenter, t.mX6, t.mY12]}>
          <AntDesign name="idcard" size={80} color={colors.purple500} style={[t.mB2]} />
          <Text>{i18n.t('idSessionDescription')}</Text>
        </Box>
      </Box>
      <Box style={[t.mX6, t.mY4]}>
        <Checkbox value={'yes'} isChecked={isPolicyAccepted} onChange={setPolicyAccepted}>
          <Box style={[t.mL3, t.wFull]}>
            <Text>{i18n.t('accept')}</Text>
            <Anchor
              href="https://assets.monei.com/docs/es/eurm-biometric-data-policy-v1.pdf"
              style={[t.textBase]}>
              {i18n.t('biometricPolicy')}
            </Anchor>
          </Box>
        </Checkbox>
      </Box>
      <Button
        style={[t.m4]}
        onPress={handleCreateIdSession}
        isLoading={loading}
        isDisabled={!isPolicyAccepted}>
        {i18n.t('createIdSession')}
      </Button>
    </SafeAreaView>
  );
};
